// Business-hours gate for OTA updates. A bundle swap reloads the webview, so we
// only apply one when the store is CLOSED (current store-local time is outside
// that day's open interval), on top of the idle gate in otaActivity.
//
// The active store's timezone + opening hours are pushed in from StoreContext
// via setBusinessHours(); the OTA scheduler reads isOutsideBusinessHours().

let businessHours = null; // { timezone, days: [{ dayOfWeek, isOpen, startTime, endTime }] }

// Provide (or clear) the active store's hours. `openingHours` is the array the
// store API returns; `timezone` is an IANA zone (e.g. 'Australia/Melbourne').
export const setBusinessHours = ({ timezone, openingHours } = {}) => {
  const days = Array.isArray(openingHours) ? openingHours : [];
  businessHours = { timezone: timezone || 'UTC', days };
};

export const clearBusinessHours = () => {
  businessHours = null;
};

// "HH:mm" -> minutes since midnight, or null if unparseable.
const parseHHMM = (value) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

// Current weekday ('Monday'...) and minutes-since-midnight in the given zone.
const localParts = (now, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const weekday = get('weekday');
    const hour = Number(get('hour')) % 24; // some engines emit '24' at midnight
    const minute = Number(get('minute'));
    return { weekday, minutes: hour * 60 + minute };
  } catch (err) {
    // Invalid timezone → fall back to the device's local time.
    return {
      weekday: now.toLocaleDateString('en-US', { weekday: 'long' }),
      minutes: now.getHours() * 60 + now.getMinutes(),
    };
  }
};

// True when it is safe (business-hours-wise) to apply an update: the store is
// closed right now. Fails OPEN (returns true) only when no hours are configured
// at all, so devices without opening-hours data aren't blocked forever; when
// hours exist but a day is ambiguous we stay conservative (return false).
export const isOutsideBusinessHours = (now = new Date()) => {
  if (!businessHours || businessHours.days.length === 0) return true;

  const { weekday, minutes } = localParts(now, businessHours.timezone);
  const entry = businessHours.days.find((d) => d.dayOfWeek === weekday);

  if (!entry) return true; // no entry for today → treated as closed
  if (!entry.isOpen) return true; // explicitly closed all day

  const start = parseHHMM(entry.startTime);
  const end = parseHHMM(entry.endTime);
  if (start == null || end == null) return false; // open but window unknown → don't risk it
  if (start === end) return false; // treat equal bounds as open all day → inside

  const inside = start < end
    ? minutes >= start && minutes < end
    : minutes >= start || minutes < end; // overnight interval (e.g. 18:00–02:00)

  return !inside;
};

// Diagnostics / tests.
export const getBusinessHours = () => businessHours;
export const __resetBusinessHours = () => {
  businessHours = null;
};
